// Releases / Overview landing page.
//
// Single-glance state of the two artifact streams (Chromium, Profiles).
// Replaces the old monolithic /releases scroll; deeper drill-ins live at
// /releases/chromium and /releases/profiles. Worker (local-api) releases
// ship as Docker images via Infrastructure > Updates, not through here.
//
// The "Upstream drift" panel is populated lazily — if the backend's
// /api/releases/chromium/upstream endpoint isn't shipped yet (404/501) the
// panel falls back to a soft "catalog not yet synced" message.

import { useQuery } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import { formatBytes } from "@veil/ui/lib/format";
import Boxes from "lucide-react/dist/esm/icons/boxes";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import Globe from "lucide-react/dist/esm/icons/globe";
import Plus from "lucide-react/dist/esm/icons/plus";
import { useMemo } from "react";
import { Link } from "wouter";
import { QueryError } from "@/components/query-state";
import { UpstreamDriftCard } from "@/components/upstream-drift-card";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";

interface ManifestMajor {
	latestBuild: string | null;
	latestProfilesVersion: string | null;
	activeProfilesVersion: string | null;
	builds: Record<string, { releaseDate: string; size: number }>;
	profiles: Record<string, unknown>;
}

interface ManifestResponse {
	success: boolean;
	manifest: {
		generatedAt: string;
		browserMajors: Record<string, ManifestMajor>;
	} | null;
}

interface ProfileReleasesResponse {
	success: boolean;
	releases: Array<{
		id: string;
		browserMajor: number;
		isActive: boolean;
		sizeBytes: number;
	}>;
}

export function ReleasesOverview() {
	const { user } = useAuth();
	const canPublish = hasRole(user, "operator");

	const manifestQuery = useQuery({
		queryKey: ["manifest"],
		queryFn: () => unwrap(api.api.manifest.get()) as Promise<ManifestResponse>,
		refetchInterval: 30_000,
	});

	const profilesQuery = useQuery({
		queryKey: ["profile-releases"],
		queryFn: async () => {
			const res = await fetch("/api/releases/profiles", {
				credentials: "same-origin",
			});
			if (!res.ok) throw new Error(`releases ${res.status}`);
			return (await res.json()) as ProfileReleasesResponse;
		},
		refetchInterval: 30_000,
	});

	const browserMajors = manifestQuery.data?.manifest?.browserMajors ?? {};
	const majors = useMemo(
		() =>
			Object.keys(browserMajors)
				.map((m) => Number.parseInt(m, 10))
				.filter((n) => Number.isFinite(n))
				.sort((a, b) => b - a),
		[browserMajors],
	);

	const chromiumCount = useMemo(
		() =>
			Object.values(browserMajors).reduce(
				(acc, m) => acc + Object.keys(m.builds).length,
				0,
			),
		[browserMajors],
	);

	const profileStats = useMemo(() => {
		const rows = profilesQuery.data?.releases ?? [];
		const total = rows.length;
		const active = rows.filter((r) => r.isActive).length;
		const size = rows.reduce((acc, r) => acc + r.sizeBytes, 0);
		return { total, active, size };
	}, [profilesQuery.data]);

	return (
		<div className="space-y-6">
			<div className="flex items-end justify-between gap-3">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Releases</h1>
					<p className="text-sm text-muted-foreground">
						Chromium builds and profile bundles published to the cluster. Worker
						images update under Infrastructure &gt; Updates.
					</p>
				</div>
				{canPublish ? (
					<div className="flex gap-2">
						<Button variant="outline" asChild>
							<Link href="/releases/new/chromium">
								<Plus className="mr-2 size-4" /> Chromium
							</Link>
						</Button>
						<Button asChild>
							<Link href="/releases/new/profiles">
								<Plus className="mr-2 size-4" /> Profiles
							</Link>
						</Button>
					</div>
				) : null}
			</div>

			<UpstreamDriftCard />

			{/* A zeroed summary card and a failed fetch look identical, so the
			    error replaces the grid rather than sitting beside it. */}
			{manifestQuery.isError || profilesQuery.isError ? (
				<QueryError
					title="Couldn't load release summaries"
					error={manifestQuery.error ?? profilesQuery.error}
					onRetry={() => {
						void manifestQuery.refetch();
						void profilesQuery.refetch();
					}}
					retrying={manifestQuery.isFetching || profilesQuery.isFetching}
				/>
			) : (
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<SummaryCard
						href="/releases/chromium"
						title="Chromium"
						icon={<Globe className="size-4" />}
						loading={manifestQuery.isPending}
						primary={`${majors.length} major${majors.length === 1 ? "" : "s"}`}
						secondary={`${chromiumCount} build${chromiumCount === 1 ? "" : "s"} published`}
						rows={majors.slice(0, 4).map((m) => ({
							label: `v${m}`,
							value: browserMajors[String(m)]?.latestBuild ? (
								<span className="font-mono text-xs">
									{browserMajors[String(m)]?.latestBuild}
								</span>
							) : (
								<Badge variant="warning">none</Badge>
							),
						}))}
					/>

					<SummaryCard
						href="/releases/profiles"
						title="Profiles"
						icon={<Boxes className="size-4" />}
						loading={profilesQuery.isPending}
						primary={`${profileStats.total} release${profileStats.total === 1 ? "" : "s"}`}
						secondary={`${profileStats.active} active · ${formatBytes(profileStats.size)} total`}
						rows={[
							{
								label: "Active per major",
								value: (
									<span className="font-mono text-xs">
										{profileStats.active}/{majors.length}
									</span>
								),
							},
						]}
					/>
				</div>
			)}
		</div>
	);
}

function SummaryCard({
	href,
	title,
	icon,
	primary,
	secondary,
	rows,
	loading,
}: {
	href: string;
	title: string;
	icon: React.ReactNode;
	primary: string;
	secondary: string;
	rows: Array<{ label: string; value: React.ReactNode }>;
	loading: boolean;
}) {
	return (
		<Card className="group/card-inner transition-colors hover:bg-muted/30">
			<CardHeader>
				<CardDescription className="flex items-center gap-2 text-xs">
					{icon}
					{title}
				</CardDescription>
				<CardTitle className="font-mono text-2xl tabular-nums">
					{loading ? <Skeleton className="h-8 w-32" /> : primary}
				</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-3">
				<p className="text-xs text-muted-foreground">{secondary}</p>
				{rows.length > 0 ? (
					<dl className="space-y-1.5 text-xs">
						{rows.map((r) => (
							<div key={r.label} className="flex items-center justify-between">
								<dt className="text-muted-foreground">{r.label}</dt>
								<dd>{r.value}</dd>
							</div>
						))}
					</dl>
				) : null}
				<Link
					href={href}
					className="mt-1 inline-flex items-center gap-1 self-start text-sm font-medium text-primary hover:underline"
				>
					View {title.toLowerCase()}
					<ChevronRight className="size-3.5" />
				</Link>
			</CardContent>
		</Card>
	);
}
